import { getUserInfo } from '../utils/tokenManager';

const resolveApiBaseUrl = () => {
  const envBase = process.env.REACT_APP_SALES_CONDITION_API_BASE_URL;
  if (envBase) return envBase;

  if (typeof window !== 'undefined') {
    const host = String(window.location?.hostname || '').toLowerCase();
    if (host === 'localhost' || host === '127.0.0.1') {
      return 'http://localhost:8081/api/v1';
    }
  }

  return 'https://inapi.tripbox.kr/api/v1';
};

const API_BASE_URL = resolveApiBaseUrl();

const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const parseCampaign = (c) => {
  if (!c || typeof c !== 'object') return null;
  if (!Number.isFinite(Number(c.campaignId))) return null;
  return {
    campaignId: toNumber(c.campaignId),
    requestId: toNumber(c.requestId),
    channel: String(c.channel || ''),
    productNo: toNumber(c.productNo),
    crsCompanyNo: toNumber(c.crsCompanyNo),
    trpCompanyNo: c.trpCompanyNo === null || c.trpCompanyNo === undefined ? null : toNumber(c.trpCompanyNo),
    status: String(c.status || ''),
    confirmedSalesConditionRevisionId:
      c.confirmedSalesConditionRevisionId === null || c.confirmedSalesConditionRevisionId === undefined
        ? null
        : toNumber(c.confirmedSalesConditionRevisionId),
    sellStartDate: String(c.sellStartDate || ''),
    sellEndDate: String(c.sellEndDate || ''),
    openedAt: c.openedAt ? String(c.openedAt) : null,
    endedAt: c.endedAt ? String(c.endedAt) : null,
  };
};

const parseDeliverable = (d) => {
  if (!d || typeof d !== 'object') return null;
  if (!Number.isFinite(Number(d.deliverableId))) return null;
  return {
    deliverableId: toNumber(d.deliverableId),
    campaignId: toNumber(d.campaignId),
    type: String(d.type || ''),
    requiredCount: toNumber(d.requiredCount),
  };
};

const parseContentItem = (i) => {
  if (!i || typeof i !== 'object') return null;
  if (!Number.isFinite(Number(i.contentItemId))) return null;
  return {
    contentItemId: toNumber(i.contentItemId),
    campaignId: toNumber(i.campaignId),
    deliverableId: i.deliverableId === null || i.deliverableId === undefined ? null : toNumber(i.deliverableId),
    status: String(i.status || ''),
    publishedUrl: String(i.publishedUrl || ''),
    urlStatus: String(i.urlStatus || ''),
    urlSubmittedAt: i.urlSubmittedAt ? String(i.urlSubmittedAt) : null,
    urlReviewAction: String(i.urlReviewAction || ''),
    urlReviewNote: String(i.urlReviewNote || ''),
    urlReviewedByUserNo:
      i.urlReviewedByUserNo === null || i.urlReviewedByUserNo === undefined ? null : toNumber(i.urlReviewedByUserNo),
    urlReviewedAt: i.urlReviewedAt ? String(i.urlReviewedAt) : null,
    createdAt: i.createdAt ? String(i.createdAt) : null,
    updatedAt: i.updatedAt ? String(i.updatedAt) : null,
  };
};

const parseContentRevision = (r) => {
  if (!r || typeof r !== 'object') return null;
  if (!Number.isFinite(Number(r.contentRevisionId))) return null;
  return {
    contentRevisionId: toNumber(r.contentRevisionId),
    contentItemId: toNumber(r.contentItemId),
    revisionNo: toNumber(r.revisionNo),
    payload: r.payload && typeof r.payload === 'object' ? r.payload : {},
    changeSummary: String(r.changeSummary || ''),
    submittedAt: r.submittedAt ? String(r.submittedAt) : null,
    reviewAction: String(r.reviewAction || ''),
    reviewNote: String(r.reviewNote || ''),
    reviewedByUserNo: r.reviewedByUserNo === null || r.reviewedByUserNo === undefined ? null : toNumber(r.reviewedByUserNo),
    reviewedAt: r.reviewedAt ? String(r.reviewedAt) : null,
  };
};

const getActorUserNo = () => {
  const { userNo } = getUserInfo() || {};
  const actorUserNo = Number(userNo || 0);
  if (!Number.isFinite(actorUserNo) || actorUserNo <= 0) {
    throw new Error('로그인 사용자 정보를 확인할 수 없습니다.');
  }
  return actorUserNo;
};

export const createCampaignFromContractRequest = async ({ requestId, trpCompanyNo, channel }) => {
  const response = await fetch(`${API_BASE_URL}/campaigns/from-contract-request/${encodeURIComponent(String(requestId))}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      trpCompanyNo: Number.isFinite(Number(trpCompanyNo)) ? Number(trpCompanyNo) : null,
      channel: String(channel || '').trim().toUpperCase()
    })
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(String(data?.message || `캠페인 생성 실패: ${response.status}`));
  }

  const campaign = parseCampaign(data?.campaign);
  if (!campaign) throw new Error('캠페인 생성 응답 형식이 올바르지 않습니다.');
  return campaign;
};

export const fetchCampaignByRequestId = async (requestId) => {
  const response = await fetch(`${API_BASE_URL}/campaigns/by-request/${encodeURIComponent(String(requestId))}/all`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' }
  });

  if (response.status === 404) return null;
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(String(data?.message || `캠페인 조회 실패: ${response.status}`));

  const items = Array.isArray(data?.items) ? data.items : [];
  if (items.length === 0) return null;

  const campaigns = items
    .map((entry) => parseCampaign(entry?.campaign))
    .filter(Boolean);
  if (campaigns.length === 0) return null;

  const deliverables = items.flatMap((entry) => {
    const list = Array.isArray(entry?.deliverables) ? entry.deliverables : [];
    return list.map(parseDeliverable).filter(Boolean);
  });

  const unmetDeliverables = items.flatMap((entry) => {
    return Array.isArray(entry?.unmetDeliverables) ? entry.unmetDeliverables : [];
  });

  const representativeCampaign = [...campaigns].sort((a, b) => {
    const aId = Number(a?.campaignId || 0);
    const bId = Number(b?.campaignId || 0);
    return aId - bId;
  })[0] || null;

  return {
    campaign: representativeCampaign,
    campaigns,
    deliverables,
    unmetDeliverables,
    isAllDeliverablesPublished: deliverables.length > 0 && unmetDeliverables.length === 0
  };
};

export const upsertDeliverable = async ({ campaignId, type, requiredCount }) => {
  const response = await fetch(`${API_BASE_URL}/campaigns/${encodeURIComponent(String(campaignId))}/deliverables`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ type: String(type || ''), requiredCount: Number(requiredCount || 1) })
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(String(data?.message || `필수 작업물 저장 실패: ${response.status}`));
  const item = parseDeliverable(data?.deliverable);
  if (!item) throw new Error('필수 작업물 저장 응답 형식이 올바르지 않습니다.');
  return item;
};

export const fetchContentItems = async (campaignId) => {
  const response = await fetch(`${API_BASE_URL}/campaigns/${encodeURIComponent(String(campaignId))}/content-items?limit=200`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' }
  });
  if (!response.ok) throw new Error(`콘텐츠 목록 조회 실패: ${response.status}`);
  const data = await response.json().catch(() => ({}));
  const items = Array.isArray(data?.items) ? data.items : [];
  return items.map(parseContentItem).filter(Boolean);
};

export const fetchContentItemDetail = async (contentItemId) => {
  const response = await fetch(`${API_BASE_URL}/content-items/${encodeURIComponent(String(contentItemId))}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' }
  });
  if (!response.ok) throw new Error(`콘텐츠 상세 조회 실패: ${response.status}`);
  const data = await response.json().catch(() => ({}));
  const item = parseContentItem(data?.item);
  const revisions = Array.isArray(data?.revisions) ? data.revisions.map(parseContentRevision).filter(Boolean) : [];
  if (!item) throw new Error('콘텐츠 상세 응답 형식이 올바르지 않습니다.');
  return { item, revisions };
};

export const reviewContentItem = async ({ contentItemId, action, reviewNote = '' }) => {
  const actorUserNo = getActorUserNo();
  const response = await fetch(`${API_BASE_URL}/content-items/${encodeURIComponent(String(contentItemId))}/review`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: String(action || '').toUpperCase(), reviewNote: String(reviewNote || ''), actorUserNo })
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(String(data?.message || `콘텐츠 검토 실패: ${response.status}`));

  const item = parseContentItem(data?.item);
  const revision = parseContentRevision(data?.revision);
  if (!item || !revision) throw new Error('콘텐츠 검토 응답 형식이 올바르지 않습니다.');
  return { item, revision };
};

export const reviewContentItemUrl = async ({ contentItemId, action, reviewNote = '' }) => {
  const actorUserNo = getActorUserNo();
  const response = await fetch(`${API_BASE_URL}/content-items/${encodeURIComponent(String(contentItemId))}/url-review`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: String(action || '').toUpperCase(), reviewNote: String(reviewNote || ''), actorUserNo })
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(String(data?.message || `URL 검토 실패: ${response.status}`));

  const item = parseContentItem(data?.item);
  if (!item) throw new Error('URL 검토 응답 형식이 올바르지 않습니다.');
  return { item };
};
